import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import type { Page } from '../lib/api';
import type {
  ActivitySummary,
  AlertView,
  CommandView,
  DeviceDetailResponse,
  EffectivePolicy,
  HardwareView,
  PackageView,
  PostureView,
} from '../lib/types';
import { useResource } from '../lib/useResource';
import { useSession } from '../lib/session';
import {
  Card, Empty, Loading, Notice, Pill, StatusPill, bytes, duration, exactly, when,
} from '../components/ui';

const COMMAND_VERBS = [
  { verb: 'LOCK', label: 'Lock the workstation', destructive: false },
  { verb: 'COLLECT_INVENTORY', label: 'Collect inventory now', destructive: false },
  { verb: 'APPLY_POLICY', label: 'Re-apply policy', destructive: false },
  { verb: 'REVOKE_SESSIONS', label: 'Sign out of company sessions', destructive: false },
  { verb: 'MESSAGE_USER', label: 'Message the user', destructive: false },
  { verb: 'RESTART', label: 'Restart the device', destructive: false },
  { verb: 'RELEASE_ISOLATION', label: 'Release network isolation', destructive: false },
  { verb: 'ISOLATE_NETWORK', label: 'Isolate from the network', destructive: true },
  { verb: 'WIPE', label: 'Erase company data', destructive: true },
];

export function DeviceDetail() {
  const { id = '' } = useParams();
  const { can } = useSession();

  const device = useResource(() => api.get<DeviceDetailResponse>(`/api/v1/devices/${id}`), [id]);
  const posture = useResource(
    () => api.get<PostureView>(`/api/v1/inventory/${id}/posture`).catch(() => null),
    [id],
  );
  const hardware = useResource(
    () => api.get<HardwareView>(`/api/v1/inventory/${id}/hardware`).catch(() => null),
    [id],
  );
  const software = useResource(
    () => api.get<Page<PackageView>>('/api/v1/inventory/software', { deviceId: id, size: 500 }),
    [id],
  );
  const alerts = useResource(
    () => api.get<Page<AlertView>>('/api/v1/alerts', { deviceId: id, size: 20 }),
    [id],
  );
  const commands = useResource(
    () => api.get<Page<CommandView>>('/api/v1/commands', { deviceId: id, size: 20 }),
    [id],
  );
  const policy = useResource(() => api.get<EffectivePolicy>(`/api/v1/policies/effective/${id}`), [id]);

  if (device.loading && !device.data) return <Loading />;
  if (device.error) return <Notice kind="error">{device.error}</Notice>;
  if (!device.data) return null;

  const summary = device.data.summary;
  // A fresh catalogue classifies almost everything as UNAPPROVED simply because it is not listed
  // yet, so PROHIBITED entries are ordered first: those are the ones that need acting on today.
  const nonApproved = (software.data?.content ?? [])
    .filter((entry) => entry.classification !== 'APPROVED')
    .sort((a, b) => {
      if (a.classification !== b.classification) return a.classification === 'PROHIBITED' ? -1 : 1;
      return a.name.localeCompare(b.name);
    });

  return (
    <>
      <div className="page-head">
        <h1>
          <span className={`dot ${summary.online ? 'on' : 'off'}`} />
          {summary.assetTag}
        </h1>
        <p>
          {summary.hostname ?? 'unknown host'} · {summary.model ?? 'unknown model'} ·{' '}
          {summary.assignedStaffName ? `assigned to ${summary.assignedStaffName}` : 'unassigned'} ·{' '}
          last seen {when(summary.lastSeenAt)}
        </p>
      </div>

      <div className="grid cols-2">
        <Card title="Device">
          <dl className="kv">
            <dt>Status</dt><dd><StatusPill value={summary.status} /></dd>
            <dt>Platform</dt>
            <dd>{summary.platform === 'MACOS' ? 'macOS' : 'Windows'} {summary.osVersion} ({device.data.osBuild ?? '—'})</dd>
            <dt>Serial number</dt><dd className="mono">{device.data.serialNumber ?? '—'}</dd>
            <dt>Manufacturer</dt><dd>{device.data.manufacturer ?? '—'}</dd>
            <dt>Architecture</dt><dd>{device.data.architecture ?? '—'}</dd>
            <dt>Agent version</dt><dd className="mono">{summary.agentVersion ?? '—'}</dd>
            <dt>Enrolled</dt><dd>{exactly(device.data.enrolledAt)}</dd>
            <dt>Last telemetry</dt><dd>{when(device.data.lastTelemetryAt)}</dd>
            <dt>Policy acknowledged</dt>
            <dd>
              {device.data.policyAcknowledgedAt
                ? exactly(device.data.policyAcknowledgedAt)
                : <Pill tone="warn">not acknowledged</Pill>}
            </dd>
          </dl>
        </Card>

        <Card title="Security posture" extra={posture.data ? `assessed ${when(posture.data.lastEvaluatedAt)}` : undefined}>
          {!posture.data && <Empty>No posture reported yet.</Empty>}
          {posture.data && (
            <>
              <div style={{ marginBottom: 14 }}>
                <Pill tone={posture.data.compliant ? 'ok' : posture.data.complianceScore < 50 ? 'stop' : 'warn'}>
                  {posture.data.compliant ? 'Meets the baseline' : `Score ${posture.data.complianceScore}/100`}
                </Pill>
                <div className={`bar ${posture.data.compliant ? '' : posture.data.complianceScore < 50 ? 'stop' : 'warn'}`}>
                  <span style={{ width: `${posture.data.complianceScore}%` }} />
                </div>
              </div>
              <dl className="kv">
                <dt>Disk encryption</dt>
                <dd>
                  <Pill tone={posture.data.diskEncryptionState === 'ENCRYPTED' ? 'ok' : 'stop'}>
                    {posture.data.diskEncryptionState.toLowerCase()}
                  </Pill>{' '}
                  {posture.data.diskEncryptionMethod ?? ''}
                </dd>
                <dt>Host firewall</dt><dd>{yesNo(posture.data.firewallEnabled)}</dd>
                <dt>Anti-malware</dt>
                <dd>{yesNo(posture.data.antivirusEnabled)} {posture.data.antivirusProduct ?? ''}</dd>
                <dt>Pending patches</dt>
                <dd>
                  {posture.data.pendingSecurityPatches == null
                    ? <span style={{ color: 'var(--faint)' }}>unknown</span>
                    : posture.data.pendingSecurityPatches}
                </dd>
                <dt>Screen lock</dt>
                <dd>
                  {posture.data.screenLockTimeoutSeconds == null
                    ? <span style={{ color: 'var(--faint)' }}>unknown</span>
                    : posture.data.screenLockTimeoutSeconds === 0
                      ? <Pill tone="stop">no password on wake</Pill>
                      : `${posture.data.screenLockTimeoutSeconds}s`}
                </dd>
                <dt>Removable storage</dt><dd>{posture.data.removableStorageMode ?? 'not enforced'}</dd>
                <dt>Failing controls</dt>
                <dd>
                  {posture.data.failingControls.length === 0
                    ? 'none'
                    : posture.data.failingControls.map((control) => (
                        <Pill key={control} tone="stop">{control.replace(/_/g, ' ').toLowerCase()}</Pill>
                      ))}
                </dd>
              </dl>
            </>
          )}
        </Card>
      </div>

      <div className="grid cols-2">
        <Card title="Hardware">
          {!hardware.data && <Empty>No hardware inventory yet.</Empty>}
          {hardware.data && (
            <dl className="kv">
              <dt>Processor</dt><dd>{hardware.data.cpuModel ?? '—'} ({hardware.data.cpuCores ?? '?'} cores)</dd>
              <dt>Memory</dt><dd>{bytes(hardware.data.totalMemoryBytes)}</dd>
              <dt>Storage</dt>
              <dd>
                {bytes(hardware.data.diskTotalBytes)} total, {bytes(hardware.data.diskFreeBytes)} free
              </dd>
              <dt>Battery health</dt>
              <dd>{hardware.data.batteryHealthPercent === null ? '—' : `${hardware.data.batteryHealthPercent}%`}</dd>
              <dt>Secure boot</dt><dd>{yesNo(hardware.data.secureBootEnabled)}</dd>
              <dt>TPM / Secure Enclave</dt><dd>{yesNo(hardware.data.tpmPresent)}</dd>
              <dt>Collected</dt><dd>{when(hardware.data.collectedAt)}</dd>
            </dl>
          )}
        </Card>

        <Card
          title="Effective policy"
          extra={policy.data ? `revision ${policy.data.revision.slice(0, 12)}` : undefined}
        >
          {policy.error && <Notice kind="error">{policy.error}</Notice>}
          {policy.data && (
            <>
              <p style={{ marginTop: 0, color: 'var(--muted)', fontSize: 13 }}>
                Assembled from {policy.data.sources.length} assignment(s), most specific last:
              </p>
              <ul style={{ margin: '0 0 12px', paddingLeft: 18, fontSize: 13 }}>
                {policy.data.sources.map((source) => (
                  <li key={`${source.policyId}-${source.scopeType}-${source.scopeValue}`}>
                    {source.name} v{source.version}{' '}
                    <span style={{ color: 'var(--faint)' }}>
                      ({source.scopeType.toLowerCase()}
                      {source.scopeValue ? ` = ${source.scopeValue}` : ''}, priority {source.priority})
                    </span>
                  </li>
                ))}
              </ul>
              <details>
                <summary style={{ cursor: 'pointer', fontSize: 13, color: 'var(--info)' }}>
                  Show the exact document this device is running
                </summary>
                <pre className="evidence" style={{ marginTop: 10 }}>
                  {JSON.stringify(policy.data.document, null, 2)}
                </pre>
              </details>
            </>
          )}
        </Card>
      </div>

      <Card
        title="Installed software"
        extra={software.data
          ? `${software.data.totalElements} installed · ${nonApproved.filter((p) => p.classification === 'PROHIBITED').length} prohibited, ${nonApproved.length} not in the approved catalogue`
          : undefined}
      >
        {software.loading && !software.data && <Loading />}
        {nonApproved.length === 0 && software.data && (
          <Empty>Every installed package matches an approved catalogue entry.</Empty>
        )}
        {nonApproved.length > 0 && (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Package</th><th>Version</th><th>Publisher</th>
                  <th>Classification</th><th>First seen</th>
                </tr>
              </thead>
              <tbody>
                {nonApproved.slice(0, 40).map((entry) => (
                  <tr key={entry.id}>
                    <td>{entry.name}</td>
                    <td className="mono">{entry.version || '—'}</td>
                    <td>{entry.publisher ?? '—'}</td>
                    <td>
                      <Pill tone={entry.classification === 'PROHIBITED' ? 'stop' : 'warn'}>
                        {entry.classification.toLowerCase()}
                      </Pill>
                    </td>
                    <td>{when(entry.firstSeenAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div className="grid cols-2">
        <Card title="Open alerts" extra={<Link to="/alerts">All alerts</Link>}>
          {alerts.data?.content.length === 0 && <Empty>No alerts for this device.</Empty>}
          {alerts.data && alerts.data.content.length > 0 && (
            <table>
              <thead><tr><th>Finding</th><th>Severity</th><th>Status</th><th>Last seen</th></tr></thead>
              <tbody>
                {alerts.data.content.map((alert) => (
                  <tr key={alert.id}>
                    <td>{alert.title}</td>
                    <td><StatusPill value={alert.severity} /></td>
                    <td><StatusPill value={alert.status} /></td>
                    <td>{when(alert.lastSeenAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <Card title="Recent commands" extra={<Link to="/commands">All commands</Link>}>
          {commands.data?.content.length === 0 && <Empty>No commands have been issued.</Empty>}
          {commands.data && commands.data.content.length > 0 && (
            <table>
              <thead><tr><th>Action</th><th>Status</th><th>Requested by</th><th>When</th></tr></thead>
              <tbody>
                {commands.data.content.map((command) => (
                  <tr key={command.id}>
                    <td>{command.verb.replace(/_/g, ' ').toLowerCase()}</td>
                    <td><StatusPill value={command.status} /></td>
                    <td>{command.requestedByName ?? '—'}</td>
                    <td>{when(command.requestedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>

      {can('TECH_ADMIN') && (
        <IssueCommand deviceId={id} assetTag={summary.assetTag} onIssued={() => commands.reload()} />
      )}

      {can('TECH_ADMIN', 'AUDITOR') && <ActivityPanel deviceId={id} />}
    </>
  );
}

function yesNo(value: boolean | null | undefined) {
  if (value === null || value === undefined) return <span style={{ color: 'var(--faint)' }}>unknown</span>;
  return <Pill tone={value ? 'ok' : 'stop'}>{value ? 'on' : 'off'}</Pill>;
}

// ---------------------------------------------------------------- issue a command

function IssueCommand({
  deviceId, assetTag, onIssued,
}: { deviceId: string; assetTag: string; onIssued: () => void }) {
  const [verb, setVerb] = useState('LOCK');
  const [reason, setReason] = useState('');
  const [message, setMessage] = useState('');
  const [wipeMode, setWipeMode] = useState('COMPANY_DATA');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ kind: 'ok' | 'error' | 'warn' | 'info'; text: string } | null>(null);

  // Polling must stop if the operator navigates away mid-flight.
  //
  // The flag is reset on mount, not just set on unmount: StrictMode mounts, unmounts and remounts
  // effects on the first render, so a cleanup-only version latches to "abandoned" before the
  // component is ever used and every poll gives up on its first tick.
  const abandoned = useRef(false);
  useEffect(() => {
    abandoned.current = false;
    return () => { abandoned.current = true; };
  }, []);

  const selected = COMMAND_VERBS.find((entry) => entry.verb === verb);

  async function submit() {
    setBusy(true);
    setResult(null);
    try {
      const parameters: Record<string, unknown> = {};
      if (verb === 'MESSAGE_USER') parameters.message = message;
      if (verb === 'WIPE') parameters.mode = wipeMode;

      const command = await api.post<CommandView>('/api/v1/commands', {
        deviceId,
        verb,
        parameters,
        reason: reason.trim(),
      });

      setReason('');
      setMessage('');
      onIssued();

      if (command.status === 'PENDING_APPROVAL') {
        setResult({
          kind: 'warn',
          text: `Requested. This action needs ${command.approvalsRequired} administrators to approve it, `
            + 'and you cannot approve your own request.',
        });
        setBusy(false);
        return;
      }

      // Issuing only queues the instruction. Reporting "sent" and stopping there is how an
      // operator ends up believing a device was restarted when the agent refused the command
      // thirty milliseconds later, so the outcome is followed to a terminal state.
      setResult({ kind: 'info', text: `Sent to ${assetTag}. Waiting for the device to confirm…` });
      const outcome = await followToCompletion(command.id);
      if (abandoned.current) return;
      setResult(outcome);
      onIssued();
    } catch (caught) {
      setResult({ kind: 'error', text: caught instanceof ApiError ? caught.message : 'Could not issue the command' });
    } finally {
      if (!abandoned.current) setBusy(false);
    }
  }

  /**
   * Polls one command until it settles.
   *
   * <p>A device that is asleep or off the network legitimately takes a while, so a timeout is
   * reported as "not confirmed yet" rather than as a failure: the command is still queued and will
   * be collected on the device's next heartbeat.
   */
  async function followToCompletion(
    commandId: string,
  ): Promise<{ kind: 'ok' | 'error' | 'warn' | 'info'; text: string }> {
    const deadline = Date.now() + 90_000;

    while (Date.now() < deadline) {
      await new Promise((resolve) => setTimeout(resolve, 1500));
      if (abandoned.current) return { kind: 'info', text: '' };

      let current: CommandView;
      try {
        current = await api.get<CommandView>(`/api/v1/commands/${commandId}`);
      } catch {
        continue;   // a transient read failure must not abandon a command that is still running
      }

      switch (current.status) {
        case 'COMPLETED': {
          const detail = current.result ? ` ${describeResult(current.result)}` : '';
          return { kind: 'ok', text: `${assetTag} confirmed the command.${detail}` };
        }
        case 'FAILED':
          return {
            kind: 'error',
            text: `${assetTag} could not carry out the command: ${current.failureReason ?? 'no reason given'}`,
          };
        case 'REJECTED':
          return { kind: 'warn', text: 'The command was rejected during approval.' };
        case 'CANCELLED':
          return { kind: 'warn', text: 'The command was cancelled.' };
        case 'EXPIRED':
          return { kind: 'warn', text: 'The command expired before the device collected it.' };
        default:
          break;   // PENDING_APPROVAL, APPROVED, DISPATCHED, ACKNOWLEDGED: still in flight
      }
    }

    return {
      kind: 'warn',
      text: `${assetTag} has not confirmed yet. The command is still queued and will be collected `
        + 'on the next check-in; watch its status in the commands list.',
    };
  }

  return (
    <Card title="Issue a command">
      {result && <Notice kind={result.kind}>{result.text}</Notice>}

      {selected?.destructive && (
        <Notice kind="warn">
          This is a destructive action. It is recorded against your name, needs a second administrator
          to approve it, and the person using the device is told when it runs.
        </Notice>
      )}

      <div className="filters">
        <label className="field">
          <span>Action</span>
          <select value={verb} onChange={(event) => setVerb(event.target.value)}>
            {COMMAND_VERBS.map((entry) => (
              <option key={entry.verb} value={entry.verb}>
                {entry.label}{entry.destructive ? ' (needs two approvals)' : ''}
              </option>
            ))}
          </select>
        </label>

        {verb === 'WIPE' && (
          <label className="field">
            <span>Scope</span>
            <select value={wipeMode} onChange={(event) => setWipeMode(event.target.value)}>
              <option value="COMPANY_DATA">Company data only</option>
              <option value="FULL_DEVICE">Full device</option>
            </select>
          </label>
        )}
      </div>

      {verb === 'MESSAGE_USER' && (
        <label className="field">
          <span>Message shown to the user</span>
          <textarea value={message} onChange={(event) => setMessage(event.target.value)} maxLength={500} />
        </label>
      )}

      <label className="field">
        <span>
          Reason
          <span className="hint">
            Required, and kept in the audit trail. Write what an auditor would need to understand
            the decision a year from now.
          </span>
        </span>
        <textarea value={reason} onChange={(event) => setReason(event.target.value)} maxLength={1000} />
      </label>

      <button
        type="button"
        className={selected?.destructive ? 'danger' : undefined}
        disabled={busy || reason.trim().length < 10 || (verb === 'MESSAGE_USER' && !message.trim())}
        onClick={submit}
      >
        {busy ? 'Sending…' : selected?.destructive ? 'Request approval' : 'Send command'}
      </button>
    </Card>
  );
}

/** Renders the structured detail an agent returns with a successful command. */
function describeResult(result: unknown): string {
  if (!result || typeof result !== 'object') return '';
  const entries = Object.entries(result as Record<string, unknown>)
    .filter(([, value]) => value !== null && value !== undefined && value !== '')
    .map(([key, value]) => (typeof value === 'boolean' ? key : `${key}: ${String(value)}`));
  return entries.length ? entries.join('; ') : '';
}

// ---------------------------------------------------------------- activity

function ActivityPanel({ deviceId }: { deviceId: string }) {
  const today = new Date();
  const monthAgo = new Date(today.getTime() - 29 * 86_400_000);
  const [from, setFrom] = useState(monthAgo.toISOString().slice(0, 10));
  const [to, setTo] = useState(today.toISOString().slice(0, 10));

  const activity = useResource(
    () => api.get<ActivitySummary>(`/api/v1/telemetry/activity/${deviceId}`, { from, to }),
    [deviceId, from, to],
  );

  return (
    <Card title="Application activity">
      {activity.data && <div className="caveat">{activity.data.interpretationNote}</div>}

      <div className="filters">
        <label className="field">
          <span>From</span>
          <input type="date" value={from} onChange={(event) => setFrom(event.target.value)} />
        </label>
        <label className="field">
          <span>To</span>
          <input type="date" value={to} onChange={(event) => setTo(event.target.value)} />
        </label>
      </div>

      {activity.error && <Notice kind="error">{activity.error}</Notice>}
      {activity.loading && !activity.data && <Loading />}
      {activity.data?.days.length === 0 && (
        <Empty>No activity recorded for this device in the selected period.</Empty>
      )}

      {activity.data && activity.data.days.length > 0 && (
        <>
          <div className="grid cols-3" style={{ marginBottom: 14 }}>
            <div className="stat">
              <div className="label">Total active time</div>
              <p className="value">{duration(activity.data.totalActiveSeconds)}</p>
            </div>
            <div className="stat">
              <div className="label">Days with activity</div>
              <p className="value">{activity.data.workingDays}</p>
            </div>
            <div className="stat">
              <div className="label">Average per active day</div>
              <p className="value">{duration(activity.data.averageActiveSecondsPerWorkingDay)}</p>
            </div>
          </div>

          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Date</th><th className="num">Active</th><th className="num">Idle</th>
                  <th className="num">Apps</th><th>First</th><th>Last</th><th>Top applications</th>
                </tr>
              </thead>
              <tbody>
                {activity.data.days.map((day) => (
                  <tr key={day.date}>
                    <td>{day.date}</td>
                    <td className="num">{duration(day.activeSeconds)}</td>
                    <td className="num">{duration(day.idleSeconds)}</td>
                    <td className="num">{day.distinctApps}</td>
                    <td>{day.firstActivityAt ? new Date(day.firstActivityAt).toLocaleTimeString() : '—'}</td>
                    <td>{day.lastActivityAt ? new Date(day.lastActivityAt).toLocaleTimeString() : '—'}</td>
                    <td>
                      {day.topApplications.slice(0, 3)
                        .map((entry) => `${entry.application} (${duration(entry.seconds)})`)
                        .join(', ') || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Card>
  );
}
